/* global Blockly */
/* global $ */
/* global Util */
import React from 'react';
import _ from 'underscore';
import { plusImage, minusImage, IMAGE_WIDTH, IMAGE_HEIGHT, getDropdownFields, addDropdownKey, removeDropdownKey, updateDropdownsAfterChange, 
    generateOutputPathCode } from '../helpers/jsonWithSchema';

/*
NOTE: This block should not be used outside the context of an ajax call.
The JS it returns depends on the namespace established for the
error or success callback of an ajax call.
 Thus this function always returns null (should never appear in the toolbox)
*/
const RequestResponseParameter = function () {
    const name = 'request_response_parameter';
    
    Blockly.Blocks[name] = {
        init: function () {
            this.jsonStructure = this.jsonStructure || {};
            this.dropdownInput = this.appendDummyInput("DROPDOWN_PATH")
                                     .appendField('', 'LABEL')
                                     .appendField(new Blockly.FieldImage(plusImage, IMAGE_WIDTH, IMAGE_HEIGHT, "*", () => this.addKey()), 'PLUS')
                                     .setOnNewRow(true)
                                     .appendField(new Blockly.FieldImage(minusImage, IMAGE_WIDTH, IMAGE_HEIGHT, "*", () => this.deleteKey()), 'DELETE')
                                     .setOnNewRow(true);
            this.setOutputShape(Blockly.OUTPUT_SHAPE_ROUND);
            let col = {};
            Object.assign(Blockly.Msg.CONNECTIONS_HUE, col);
            col.primary = '#b10069';
            this.setColour(col);
            this.setOutput(true, null);
            this.setTooltip('Get the value of an API response.');
            this.setHelpUrl(Blockly.BASE_HELP_URL + "#connections");
        },

        mutationToDom: function () {
            const path = getDropdownFields(this.dropdownInput).map(field => field.getValue());
            const mutation = Util.dom('mutation', {label: this.getFieldValue('LABEL'), path: path, parent_id: this.parent_id});
            return mutation;
        },

        domToMutation: function (xml) {
            const label = (xml.getAttribute('label') || this.getFieldValue('LABEL'));
            this.setLabel(label);
            this.path = xml.getAttribute('path');
            this.path = this.path ? this.path.split(',') : [];
            this.parent_id = xml.getAttribute('parent_id');

            // set jsonStructure if its parent still has it
            if (this.workspace.getBlockById(this.parent_id) != null) {
                let parentBlock = this.workspace.getBlockById(this.parent_id);
                if (parentBlock.type.indexOf('rest_service') >= 0 && parentBlock.responseJsonStructure != null) {
                    this.jsonStructure = parentBlock.responseJsonStructure;
                    if (Object.keys(this.jsonStructure).length != 0) {
                        this.xmlToBlock(this.jsonStructure, this.path, 0);
                    }
                }
            }

            // create drop down with path if no jsonStructure set
            if (Object.keys(this.jsonStructure).length === 0) {
                this.createResponseDropdown(this.path);
            }
        },

        createResponseDropdown: function (path) {
            this.dropdownInput.removeField('PLUS');
            this.dropdownInput.removeField('DELETE');
            for (let i = 0; i < path.length; i++) {
                let ddOptions = [[path[i], path[i]]];
                let currentVal = ddOptions[0][0];
                let dd = new Blockly.FieldDropdown(ddOptions);
                dd.setValue(currentVal);
                this.dropdownInput.appendField(dd, i);
            }
            this.dropdownInput.appendField(new Blockly.FieldImage(plusImage, IMAGE_WIDTH, IMAGE_HEIGHT, '*', () => this.addKey()), 'PLUS')
                .setOnNewRow(true)
                .appendField(new Blockly.FieldImage(minusImage, IMAGE_WIDTH, IMAGE_HEIGHT, '*', () => this.deleteKey()), 'DELETE')
                .setOnNewRow(true);
        },

        setLabel: function (label) {
            this.getField('LABEL').setValue(label);
        },

        setParentId: function (id) {
            this.parent_id = id;
        },

        setJsonStructure: function (json) {
            this.jsonStructure = json;
        },

        addKey: function (selectedValue = null) {
            const afterRenderCallback = () => {
                if (this.parentBlock_ && this.parentBlock_.changeHandler) {
                    this.parentBlock_.changeHandler();
                }
            };

            addDropdownKey({
                input: this.dropdownInput,
                jsonStructure: this.jsonStructure,
                selectedValue: selectedValue,
                updateCallback: this.update.bind(this),
                afterRenderCallback: afterRenderCallback,
                plusClick: () => this.addKey(),
                minusClick: () => this.deleteKey()
            });
        },

        deleteKey: function () {
            const afterRenderCallback = () => {
                if (this.parentBlock_ && this.parentBlock_.changeHandler) {
                    this.parentBlock_.changeHandler();
                }
            };

            removeDropdownKey({
                input: this.dropdownInput,
                jsonStructure: this.jsonStructure,
                afterRenderCallback: afterRenderCallback
            });
        },

        update: function (level) {
            updateDropdownsAfterChange({
                input: this.dropdownInput,
                jsonStructure: this.jsonStructure,
                changedDropdownIndex: level
            });
        },

        xmlToBlock: function (json, path, depth) {
            if (this.jsonStructure && this.path.length > 0) {
                for (let i = 0; i < path.length; i++) {
                    this.addKey(path[i]);
                }
            } else {
                this.dropdownInput.removeField('PLUS');
                this.dropdownInput.removeField('DELETE');
                this.specToBlock(json, path, depth);
                this.dropdownInput.appendField(new Blockly.FieldImage(plusImage, IMAGE_WIDTH, IMAGE_HEIGHT, '*', () => this.addKey()), 'PLUS')
                    .setOnNewRow(true)
                    .appendField(new Blockly.FieldImage(minusImage, IMAGE_WIDTH, IMAGE_HEIGHT, '*', () => this.deleteKey()), 'DELETE')
                    .setOnNewRow(true);
                this.initSvg();
                this.render();
            }
        },

        // json: structure at this depth
        // path: path through which to traverse json structure. can be undefined
        // depth: depth of json structure/dds, 0 = top level. so first dd = depth of 0
        specToBlock: function (json, path, depth) {
            const dropdownFields = getDropdownFields(this.dropdownInput);

            // necessary to clone path (otherwise .splice(0, 1) below gets confusing)
            path = [].concat(path) || [];

            // remove all fields greater than current depth (dd's to the right)
            const fieldsToRemove = [];
            for (let i = 0; i < dropdownFields.length; i++) {
                if (parseFloat(dropdownFields[i].name) >= depth) {
                    fieldsToRemove.push(dropdownFields[i].name);
                }
            }
            fieldsToRemove.forEach(field => this.dropdownInput.removeField(field));

            // check if there should be another dd or maybe we hit a node/value;
            if (!!json && typeof(json) === 'object' && Object.keys(json).length && !_.isEmpty(path)) {
                const ddOptions = Object.keys(json).map(function (x) {return [x, x]}.bind(this));
                const currentVal = path.splice(0, 1)[0] || ddOptions[0][0];
                const dd = new Blockly.FieldDropdown(function () {return ddOptions}, function (value) {
                    this.specToBlock(json[value], path, depth + 1)
                }.bind(this));
                dd.setValue(currentVal);
                this.dropdownInput.appendField(dd, depth);
                this.specToBlock(json[currentVal], path, depth + 1);
            }
        }
    };

    Blockly.JavaScript[name] = function (block) {
        /*
            This will produce the structure list (which will be used by other blocks) and
            a valid ES5 code, which chains all the selected parameters.

            Please note that we can't use ES6 optional chaining syntax (?.), so we need to 
            use old bulky syntax.

            So if the field list consists of "A", "B" and "C", then 

            The result structure will be:
            A|B|C

            The result code will be:
            (ResponseScope['ID'] && ResponseScope['ID']['A'] && ResponseScope['ID']['A']['B'] && ResponseScope['ID']['A']['B']['C']) ?? ""
        */
        const dropdownFields = getDropdownFields(block.dropdownInput);

        return generateOutputPathCode({
            baseExpression: `ResponseScope['${this.parent_id}']`,
            dropdownFields: dropdownFields,
            includeStructureComment: true
        });
    };

    // this should never show in the toolbox
    return null;
};

export default RequestResponseParameter;
